import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CompositeModel } from '../../models/complex/composite.model';
import { DropdownModel } from '../../models/complex/dropdown.model';
import { RadioModel } from '../../models/complex/radio.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { DropdownModelOptions } from '../../models/options/dropdown-model-options';
import { TextModelOptions } from '../../models/options/text-model-options';
import { MultilineTextModel } from '../../models/primitive/multiline-text.model';
import { TextModel } from '../../models/primitive/text.model';
import { TimeModel } from '../../models/primitive/time.model';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { RouterHelperService } from '../../services/router-helper/router-helper.service';
import { UserGidService } from '../../services/user-gid/user-gid.service';
import { MultilineTextModelOptions } from '../../models/options/multiline-text-model-options';
import { IncidentPriority, IncidentType } from '../../types/sharp-incidents';
import { BooleanModel } from '../../models/primitive/boolean.model';
import { BooleanModelOptions } from '../../models/options/boolean-model-options';

export class ReportIssueModel extends CompositeModel {
    public gid: TextModel;
    public issue_description: TextModel;
    public notification_num: TextModel;
    public material_num: TextModel;
    public form_id: TextModel;
    public date_time: TimeModel;
    public error_message: TextModel;
    public actual_behaviour: TextModel;
    public expected_behaviour: TextModel;
    public steps_to_reproduce: MultilineTextModel;
    public personal_urgency: DropdownModel<TextModel>;
    public category: RadioModel<TextModel>;
    public email: string;
    public files: File[];
    public phiAgreement: BooleanModel;
    public constructor(errorMessage = '') {
        super('ReportIssueModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const routerHelper = injector.get(RouterHelperService);
        const configService = injector.get(ConfigService);
        const userGidService = injector.get(UserGidService);

        this.issue_description = new TextModel(
            'issue_description',
            '',
            new TextModelOptions({ required: true, minLength: 15, maxLength: 160 }),
        );
        this.error_message = new TextModel('error_message', errorMessage);

        // get  the active route and the parameters. If the url is matching the details/confirmation/forms url, then we
        // need to pre-fill  the notification number in the model. For more details read comments of the ticket SHCNGMP58-1320
        const url = routerHelper.currentUrl;
        const params = routerHelper.params;
        const notificationIdentifierModel =
            url.indexOf(ConfigConstants.routeUrlDetails) > 0 ||
            url.indexOf(ConfigConstants.routeUrlConfirmation) > 0 ||
            url.indexOf(ConfigConstants.routeUrlForms) > 0
                ? new NotificationIdentifierModel(params.frontid, params.qmnum, params.manum)
                : undefined;

        const notificationNo = notificationIdentifierModel
            ? `${notificationIdentifierModel.getQmnum()} (${notificationIdentifierModel.getManum()})`
            : '';
        this.notification_num = new TextModel('notification_num', notificationNo);
        this.material_num = new TextModel('material_num');
        const formId = url.indexOf(ConfigConstants.routeUrlForms) > 0 ? params.formid : '';
        this.form_id = new TextModel('form_id', formId);
        this.date_time = new TimeModel('date_time', moment());
        this.actual_behaviour = new TextModel('actual_behaviour');
        this.expected_behaviour = new TextModel('expected_behaviour');
        this.phiAgreement = new BooleanModel('phiAgreement', false, new BooleanModelOptions({ required: true }));
        const placeHolderForStepsToReproduce = <string>(
            translate.instant('REPORT_ISSUE_FIELD_STEPS_TO_REPRODUCE_PLACEHOLDER')
        );
        this.steps_to_reproduce = new MultilineTextModel(
            'steps_to_reproduce',
            '',
            new MultilineTextModelOptions({
                required: true,
                minLength: 15,
                placeholder: placeHolderForStepsToReproduce,
            }),
        );

        const options: Array<DisplayDecorator<TextModel>> = [];
        const totallyBlockedDisplayName = <string>(
            translate.instant('REPORT_ISSUE_FIELD_PERSONAL_URGENCY_TOTALLY_BLOCKED')
        );
        const partiallyBlockedDisplayName = <string>(
            translate.instant('REPORT_ISSUE_FIELD_PERSONAL_URGENCY_PARTIALLY_BLOCKED')
        );
        const notBlockedDisplayName = <string>translate.instant('REPORT_ISSUE_FIELD_PERSONAL_URGENCY_NOT_BLOCKED');
        options.push(
            new DisplayDecorator(
                totallyBlockedDisplayName,
                new TextModel(totallyBlockedDisplayName, IncidentPriority.TOTALLY_BLOCKED),
            ),
        );
        options.push(
            new DisplayDecorator(
                partiallyBlockedDisplayName,
                new TextModel(partiallyBlockedDisplayName, IncidentPriority.PARTIALLY_BLOCKED),
            ),
        );
        options.push(
            new DisplayDecorator(
                notBlockedDisplayName,
                new TextModel(notBlockedDisplayName, IncidentPriority.NOT_BLOCKED),
            ),
        );

        this.personal_urgency = new DropdownModel<TextModel>(
            'personal_urgency',
            options,
            new DropdownModelOptions({ required: true }),
        );

        const categoryOptions: Array<DisplayDecorator<TextModel>> = [];
        categoryOptions.push(
            new DisplayDecorator(
                <string>translate.instant('REPORT_ISSUE_FIELD_CATEGORY_FAILURE'),
                new TextModel(IncidentType.FAILURE, IncidentType.FAILURE),
            ),
        );
        categoryOptions.push(
            new DisplayDecorator(
                <string>translate.instant('REPORT_ISSUE_FIELD_CATEGORY_REQUEST'),
                new TextModel(IncidentType.REQUEST, IncidentType.REQUEST),
            ),
        );

        this.category = new RadioModel<TextModel>('category', categoryOptions);

        const gid = userGidService.getUserGid();
        const isGidReadonly = typeof gid !== 'undefined' && gid !== '';
        this.gid = new TextModel(
            'gid',
            gid,
            new TextModelOptions({ required: true, minLength: 8, maxLength: 8, readonly: isGidReadonly }),
        );

        this.email = configService.isInitialized ? configService.getCseEmailFax() : '';
    }

    public getGID(): string {
        return this.gid.value.toUpperCase();
    }
}
